import React, { useState, useEffect } from 'react';
import { userAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { 
  Users, Plus, Trash2, User, Mail, Phone, Shield, Key
} from 'lucide-react';

const PERMISSIONS = [
  { id: 'leads', label: 'Manage Leads', description: 'View and update leads' },
  { id: 'site_visits', label: 'Site Visits', description: 'Schedule and manage site visits' },
  { id: 'client_tagging', label: 'Client Tagging', description: 'Tag and manage clients' },
  { id: 'commissions', label: 'View Commissions', description: 'View commission records' },
  { id: 'microsites', label: 'Microsites', description: 'Create and edit microsites' },
  { id: 'analytics', label: 'Analytics', description: 'View performance analytics' },
];

const SubUsersPage = () => {
  const { user } = useAuth();
  const [subUsers, setSubUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    permissions: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await userAPI.listSubUsers();
      setSubUsers(res.data);
    } catch (error) {
      toast.error('Failed to load sub-users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await userAPI.createSubUser(newUser);
      toast.success('Sub-user created successfully');
      setDialogOpen(false);
      setNewUser({ name: '', email: '', phone: '', password: '', permissions: [] });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create sub-user');
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteDialog.user) return;
    try {
      await userAPI.deleteSubUser(deleteDialog.user.id);
      toast.success('Sub-user deleted');
      setDeleteDialog({ open: false, user: null });
      loadData();
    } catch (error) {
      toast.error('Failed to delete sub-user');
    }
  };

  const togglePermission = (permId) => {
    setNewUser(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permId)
        ? prev.permissions.filter(p => p !== permId)
        : [...prev.permissions, permId]
    }));
  };

  const isBuilder = user?.role === 'builder_admin';
  const isPartner = user?.role === 'partner';

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sub-users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-7 w-7 text-blue-600" />
            {isBuilder ? 'Team Members' : 'My Team'}
          </h1>
          <p className="text-muted-foreground">
            {isBuilder 
              ? 'Manage your sales team and staff members' 
              : 'Add team members to help manage your leads'}
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-add-user">
              <Plus className="h-4 w-4 mr-2" />
              Add Team Member
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Team Member</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input 
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  placeholder="John Doe"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input 
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  placeholder="john@example.com"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input 
                  value={newUser.phone}
                  onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                  placeholder="+91 9876543210"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input 
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  placeholder="Create a password"
                  required
                />
              </div>
              
              <div className="space-y-3">
                <Label>Permissions</Label>
                <div className="space-y-2">
                  {PERMISSIONS.map(perm => (
                    <div 
                      key={perm.id} 
                      className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                      onClick={() => togglePermission(perm.id)}
                    >
                      <Checkbox 
                        checked={newUser.permissions.includes(perm.id)}
                        onCheckedChange={() => togglePermission(perm.id)}
                      />
                      <div>
                        <p className="font-medium text-sm">{perm.label}</p>
                        <p className="text-xs text-muted-foreground">{perm.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <Button type="submit" className="w-full">
                Create Team Member
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Team Members Grid */}
      {subUsers.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No team members yet</p>
            <p className="text-muted-foreground mb-4">Add team members to help manage your work</p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Team Member
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {subUsers.map(subUser => (
            <Card key={subUser.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <User className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{subUser.name}</h3>
                      <Badge variant="secondary" className="text-xs">Sub-User</Badge>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setDeleteDialog({ open: true, user: subUser })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="space-y-2 text-sm">
                  <p className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    {subUser.email}
                  </p>
                  {subUser.phone && (
                    <p className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      {subUser.phone}
                    </p>
                  )}
                </div>
                
                {subUser.permissions?.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                      <Shield className="h-3 w-3" />
                      Permissions
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {subUser.permissions.map(perm => (
                        <Badge key={perm} variant="outline" className="text-xs">
                          {PERMISSIONS.find(p => p.id === perm)?.label || perm}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {(!subUser.permissions || subUser.permissions.length === 0) && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Key className="h-3 w-3" />
                      Full access to all features
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, user: open ? deleteDialog.user : null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Team Member</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Are you sure you want to remove <strong>{deleteDialog.user?.name}</strong> from your team? 
            This action cannot be undone.
          </p>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, user: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubUsersPage;
